/**
 * CheckInRow (BON-204 R2) — one row in the CheckInModal: customer name as
 * "Last, First" + a "Party of N" subtext, with a right-hand "{count}/{N}" pill.
 * No avatar. The whole row is a single tappable button (mobile-friendly hit
 * target). Tap routing: a party-of-1 row toggles 0↔1 inline (`onToggle`); a
 * party>1 row opens the stepper (`onOpenStepper`). Pure presentation — the
 * parent owns the booking list, sorting, and the mutations.
 *
 * Pill states: fully in → solid green + check; partial (0 < count < N) →
 * green-outline; zero party-of-1 → empty circle (a tap target); zero party>1 →
 * outlined "0/N".
 */
import { Check } from 'lucide-react';

import type { RosterBooking } from '../../../../api/workshops';

import { displayName, isFullyCheckedIn, isPartyOfOne } from './checkInModel';

export interface CheckInRowProps {
  booking: RosterBooking;
  /** Disabled while a mutation for this row is in flight. */
  pending: boolean;
  /** Party-of-1 inline toggle → the next count (0 or 1). */
  onToggle: (booking: RosterBooking, nextCount: number) => void;
  /** Party>1 → open the stepper bottom-sheet for this booking. */
  onOpenStepper: (booking: RosterBooking) => void;
}

function Pill({ booking }: { booking: RosterBooking }) {
  const { checkedInCount, attendeeCount } = booking;
  const full = isFullyCheckedIn(booking);
  const empty = checkedInCount === 0;

  // Zero + party-of-1: an empty circle tap target (no count text).
  if (empty && isPartyOfOne(booking)) {
    return (
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 30,
          height: 30,
          borderRadius: 999,
          border: '2px solid var(--nb-border)',
          background: 'transparent',
        }}
      />
    );
  }

  const label = `${checkedInCount}/${attendeeCount}`;
  return (
    <span
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 44,
        height: 30,
        padding: '0 10px',
        borderRadius: 999,
        justifyContent: 'center',
        fontSize: 12.5,
        fontWeight: 700,
        color: full ? '#fff' : 'var(--nb-primary)',
        background: full ? 'var(--nb-primary)' : 'transparent',
        border: full ? 'none' : '1.5px solid var(--nb-primary)',
      }}
    >
      {full && <Check size={13} strokeWidth={3} aria-hidden="true" />}
      {label}
    </span>
  );
}

export function CheckInRow({ booking, pending, onToggle, onOpenStepper }: CheckInRowProps) {
  const { isComp, attendeeCount, checkedInCount } = booking;
  const handleClick = () => {
    if (isPartyOfOne(booking)) onToggle(booking, checkedInCount === 0 ? 1 : 0);
    else onOpenStepper(booking);
  };

  return (
    <button
      type="button"
      data-testid="check-in-row"
      data-checked-in={isFullyCheckedIn(booking) ? 'true' : 'false'}
      data-count={checkedInCount}
      disabled={pending}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minHeight: 52,
        padding: '8px 12px',
        border: 'none',
        borderRadius: 12,
        textAlign: 'left',
        cursor: pending ? 'default' : 'pointer',
        font: 'inherit',
        color: 'var(--nb-ink)',
        background: 'transparent',
        opacity: pending ? 0.6 : 1,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 14.5,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName(booking)}
          {isComp && (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--nb-warn)', fontWeight: 600 }}>
              · Comp
            </span>
          )}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--nb-subtle)' }}>
          Party of {attendeeCount}
        </span>
      </span>
      <Pill booking={booking} />
    </button>
  );
}
